import { isBrowser } from '../universal';
import { globalVar } from 'app/infrastructure/universal/runtime';
import { fetchAttachment } from '../remote-data/http';
import { escapeHtml, escapeUrl } from 'app/components/favourite-layout/favourite/common/utils';

/**
 * Centralized handling of external redirects with guard checks for server side render.
 * Uses location.href when running in a browser
 *
 * @export
 * @param {string} url - The url to redirect to
 */
export function externalRedirect(url : string, newWindow = false) {
  if (isBrowser) {
    const escapedUrl = escapeUrl(url);
    if(newWindow) {
      window.open(escapedUrl, '_blank', 'noopener,noreferrer');
    } else {
      location.href = escapedUrl;
    }
  }
}

/**
 * Queues a rAF within a second rAF to ensure it will be executed in the next browser paint frame.​
 *
 * This can be used to align with updates from React´s internal scheduling
 * mechanisms, e.g: instead of using setTimeout to wait for DOM elements to paint and
 * be reachable by document.getElementById
 *
 * @param fn a function containing work to perform before next repaint
 */
export const scheduleUpdate = (fn: ()=>void) => {
  requestAnimationFrame( () =>
    requestAnimationFrame(fn)
  );
};

export const createDownloader = (url: string) => async (blobId: number, name: string): Promise<void> => {
  const blob = await fetchAttachment(url)(blobId, false);
  if (!blob) {
    return;
  }
  const objectUrl = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = escapeUrl(objectUrl);
  anchor.download = escapeHtml(name);
  // Firefox requires the anchor to be added in the document
  document.body.appendChild(anchor);
  anchor.click();
  setTimeout(() => {
    // Firefox needs this to be in a timeout
    document.body.removeChild(anchor);
    window.URL.revokeObjectURL(objectUrl);
  }, 0);
};

/**
 *
 * @param value value to copy
 * @returns Status if the copy was a success
 *
 */
export const copyToClipboard = (value: string) : boolean => {
  try {
    const textField = document.createElement('textarea');
    textField.innerText = value;
    textField.style.opacity = '0';
    document.body.appendChild(textField);
    textField.select();
    document.execCommand('copy');
    document.body.removeChild(textField);
    return true;
  } catch (err) {
    return false;
  }
};

/**
 * Detect if browser is Firefox
 *
 * @returns returns true if the browser is firefox
 */
export const isFirefox = () => {
  return navigator.userAgent.toLowerCase().indexOf('firefox') > -1;
};

/**
 * Detect if browser is Edge on iOS devices
 *
 * @returns returns true if the browser is Edge on iOS device
 */
export const isEdgeOnIos = globalVar('navigator').userAgent?.indexOf("EdgiOS") !== -1;
